import InfoTip from "../../InfoTip";
import "./drawerStats.css";
import { grammarBenchmark, useGrammarBenchmarks } from "./useGrammarBenchmarks";

const PARTS = [
  { key: "pct_he", label: "Higher education", color: "#00515b" },
  { key: "pct_fe", label: "Further education", color: "#6283c4" },
  { key: "pct_other_education", label: "Other education", color: "#8eabc0" },
  { key: "pct_apprenticeship", label: "Apprenticeship", color: "#c4a15a" },
  { key: "pct_employment", label: "Employment", color: "#c46a3a" },
  { key: "pct_not_sustained", label: "Not sustained", color: "#d5dde2" },
  { key: "pct_unknown", label: "Not captured", color: "#eef2f4" },
];

function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function fmt(value) {
  const n = num(value);
  return n == null ? "—" : `${n.toFixed(1)}%`;
}

export default function DestinationGraphic({ row }) {
  const { rows, loading, error } = useGrammarBenchmarks(true);
  const parts = PARTS.map((part) => ({ ...part, value: num(row[part.key]) })).filter((part) => part.value != null);
  const total = parts.reduce((sum, part) => sum + part.value, 0);
  const he = num(row.pct_he);
  const heBench = grammarBenchmark(rows, "destYear", "university", row.year_tab);

  return (
    <div className="ds-picture">
      <p className="ds-dest-lead">
        Where the {row.leavers_student_count != null ? `${row.leavers_student_count} ` : ""}leavers went after 16–18
        {row.academic_year_label ? `, ${row.academic_year_label}` : ""}.
      </p>
      {total > 0 ? (
        <div
          className="ds-stack"
          role="img"
          aria-label={parts.map((part) => `${part.label} ${fmt(part.value)}`).join(", ")}
        >
          {parts.map((part) => (
            <span
              key={part.key}
              className="ds-stack-seg"
              style={{
                width: `${(part.value / total) * 100}%`,
                minWidth: part.value > 0 ? 2 : 0,
                background: part.color,
                color: part.key === "pct_unknown" || part.key === "pct_not_sustained" || part.key === "pct_apprenticeship" ? "#0a3340" : "#f4f8f6",
              }}
            />
          ))}
        </div>
      ) : null}
      <ul className="ds-dest-list">
        {parts.map((part) => (
          <li key={part.key}>
            <i style={{ background: part.color }} />
            <span>{part.label}</span>
            <strong>{fmt(part.value)}</strong>
          </li>
        ))}
      </ul>

      {he != null ? (
        <div className="ds-he">
          <div className="ds-bar-label">
            <span>Higher education</span>
            <strong>{fmt(he)}</strong>
          </div>
          <div
            className="ds-bar-track"
            role="img"
            aria-label={
              heBench
                ? `Higher education: this school ${fmt(he)}, grammar mean ${fmt(heBench.mean)}`
                : `Higher education: this school ${fmt(he)}`
            }
          >
            <span className="ds-bar-fill" style={{ width: `${Math.min(100, he)}%` }} />
            {heBench ? <span className="ds-bar-mean" style={{ left: `${Math.min(100, heBench.mean)}%` }} /> : null}
          </div>
          <div className="ds-legend">
            <span><i className="ds-swatch ds-swatch--school" /> This school</span>
            <span><i className="ds-swatch ds-swatch--mean" /> Grammar mean</span>
          </div>
          {loading ? <p className="ds-hint">Loading the grammar mean…</p> : null}
          {error ? <p className="ds-hint">The grammar mean did not load.</p> : null}
          {heBench ? (
            <p className="ds-hint">
              <span className="v2-info-tip--label">
                Grammar mean
                <InfoTip
                  text={
                    heBench.sameYear
                      ? `Mean of ${heBench.count} grammar schools with a ${row.year_tab} higher-education figure.`
                      : `Mean of ${heBench.count} grammar schools, each using its latest stored higher-education figure.`
                  }
                />
              </span>
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
